import { defineStore } from 'pinia'
import { StorageManager, StorageKey } from '@/@vxapp/@core/AppStorage/StorageManager'

/**
 * Cache entry structure stored in memory and localStorage
 * @typedef {Object} CacheEntry
 * @property {any} value - The cached value
 * @property {number} expiresAt - Timestamp when this entry expires
 */

/**
 * Cache entries container structure
 * @typedef {Object.<string, CacheEntry>} CacheEntriesContainer
 */

/**
 * Robust deep clone utility that handles circular references, functions, and non-serializable objects
 * This ensures cached values cannot be mutated and prevents DataCloneError issues
 *
 * @param {any} obj - The object to clone
 * @param {WeakMap} [visited] - Internal tracker for circular references (for recursion)
 * @param {number} [depth] - Current recursion depth (prevents stack overflow)
 * @param {number} [maxDepth] - Maximum recursion depth (default: 100)
 * @returns {any} Deep cloned object
 */
function robustDeepClone(obj, visited = new WeakMap(), depth = 0, maxDepth = 100) {
  // Prevent infinite recursion
  if (depth > maxDepth) {
    console.warn('[useApiCacheStore][robustDeepClone] Maximum depth reached, returning original')

    return obj
  }

  // Handle null and undefined
  if (obj === null || obj === undefined) {
    return obj
  }

  // Handle primitives (string, number, boolean, symbol, bigint)
  if (typeof obj !== 'object') {
    return obj
  }

  // Handle circular references
  if (visited.has(obj)) {
    // Return a placeholder to break the cycle
    return { __circularRef: true }
  }

  // Handle Date objects
  if (obj instanceof Date) {
    return new Date(obj.getTime())
  }

  // Handle RegExp objects
  if (obj instanceof RegExp) {
    return new RegExp(obj.source, obj.flags)
  }

  // Handle Map objects
  if (obj instanceof Map) {
    const clonedMap = new Map()

    visited.set(obj, clonedMap)
    for (const [key, value] of obj.entries()) {
      clonedMap.set(
        robustDeepClone(key, visited, depth + 1, maxDepth),
        robustDeepClone(value, visited, depth + 1, maxDepth)
      )
    }

    return clonedMap
  }

  // Handle Set objects
  if (obj instanceof Set) {
    const clonedSet = new Set()

    visited.set(obj, clonedSet)
    for (const value of obj.values()) {
      clonedSet.add(robustDeepClone(value, visited, depth + 1, maxDepth))
    }

    return clonedSet
  }

  // Handle Array objects
  if (Array.isArray(obj)) {
    const clonedArray = []

    visited.set(obj, clonedArray)
    for (let i = 0; i < obj.length; i++) {
      clonedArray[i] = robustDeepClone(obj[i], visited, depth + 1, maxDepth)
    }

    return clonedArray
  }

  // Handle ArrayBuffer
  if (obj instanceof ArrayBuffer) {
    return obj.slice(0)
  }

  // Handle TypedArrays (Uint8Array, Int32Array, etc.)
  // Check if it's a TypedArray by checking if it has a buffer property
  if (obj.buffer instanceof ArrayBuffer && typeof obj.constructor === 'function') {
    try {
      return new obj.constructor(obj)
    } catch (e) {
      // If constructor fails, try slice
      try {
        return obj.slice(0)
      } catch (sliceErr) {
        console.warn(
          '[useApiCacheStore][robustDeepClone] Failed to clone TypedArray, returning serializable representation'
        )

        return Array.from(obj)
      }
    }
  }

  // Handle DOM elements and other non-serializable browser objects
  // These typically can't be cloned, so we return a serializable representation
  if (obj instanceof HTMLElement || obj instanceof Node) {
    console.warn(
      '[useApiCacheStore][robustDeepClone] DOM element detected, returning serializable representation'
    )

    return {
      __nonSerializable: true,
      __type: 'HTMLElement',
      __tagName: obj.tagName || 'UNKNOWN',
      __toString: obj.toString ? obj.toString() : '[HTMLElement]',
    }
  }

  // Handle Error objects
  if (obj instanceof Error) {
    return {
      __error: true,
      name: obj.name,
      message: obj.message,
      stack: obj.stack,
    }
  }

  // Handle Promise objects (shouldn't be in API responses, but handle gracefully)
  if (
    obj instanceof Promise ||
    (typeof obj.then === 'function' && typeof obj.catch === 'function')
  ) {
    console.warn(
      '[useApiCacheStore][robustDeepClone] Promise object detected, cannot be cloned - returning placeholder'
    )

    return {
      __nonSerializable: true,
      __type: 'Promise',
      __message: 'Promise objects cannot be cloned',
    }
  }

  // Handle plain objects
  // First, try structuredClone (fastest, handles most cases including nested objects)
  if (typeof structuredClone === 'function') {
    try {
      // If structuredClone succeeded, return it immediately
      // It handles all nested objects recursively, so we don't need manual cloning
      return structuredClone(obj)
    } catch (structuredCloneErr) {
      // structuredClone failed - this means the object contains non-cloneable data
      // Fall through to manual cloning which will handle it gracefully
      if (
        structuredCloneErr.name !== 'DataCloneError' &&
        structuredCloneErr.name !== 'DOMException'
      ) {
        // Non-DataCloneError might indicate a different issue, but we'll still try manual cloning
        console.warn(
          '[useApiCacheStore][robustDeepClone] structuredClone failed with non-DataCloneError, attempting manual clone:',
          structuredCloneErr.name
        )
      }

      // DataCloneError or DOMException - proceed to manual cloning
    }
  }

  // Manual cloning for objects that structuredClone can't handle
  // This handles cases where structuredClone fails due to non-cloneable properties
  const clonedObj = {}

  visited.set(obj, clonedObj)

  // Try to get all properties (including non-enumerable ones if possible)
  let keys = Object.keys(obj)
  try {
    // Also try to get symbol keys
    const symbolKeys = Object.getOwnPropertySymbols(obj)
    if (symbolKeys.length > 0) {
      // Convert symbols to strings for serialization (they'll be lost but won't break)
      symbolKeys.forEach(sym => {
        try {
          keys.push(String(sym))
        } catch (e) {
          // Ignore symbol keys that can't be converted
        }
      })
    }
  } catch (e) {
    // Ignore errors getting symbol keys
  }

  for (const key of keys) {
    try {
      const descriptor = Object.getOwnPropertyDescriptor(obj, key)
      if (descriptor) {
        // Skip functions - they can't be serialized and shouldn't be in cached data
        if (typeof descriptor.value === 'function') {
          continue
        }

        // Skip getters/setters - they can't be cloned
        if (descriptor.get || descriptor.set) {
          continue
        }
      }

      const value = obj[key]

      // Skip functions
      if (typeof value === 'function') {
        continue
      }

      // Clone the value recursively
      clonedObj[key] = robustDeepClone(value, visited, depth + 1, maxDepth)
    } catch (propErr) {
      // If we can't clone a property, skip it (better than failing entirely)
      console.warn(
        `[useApiCacheStore][robustDeepClone] Failed to clone property "${key}", skipping:`,
        propErr
      )
    }
  }

  // Preserve prototype chain information if possible (for debugging)
  if (obj.constructor && obj.constructor !== Object) {
    try {
      clonedObj.__constructorName = obj.constructor.name
    } catch (e) {
      // Ignore errors
    }
  }

  return clonedObj
}

export const useApiCacheStore = defineStore('useApiCacheStore', {
  state: () => ({
    // In-memory cache for fast access (kept in sync with localStorage)
    entries: new Map(),
    defaultTtlMs: 10000,

    // Track if we've loaded from storage on initialization
    _initialized: false,
  }),

  actions: {
    /**
     * Loads cache entries from localStorage into memory
     * This is called automatically on first access, but can be called manually
     * @returns {boolean} True if loaded successfully, false otherwise
     */
    _loadFromStorage() {
      if (this._initialized) {
        return true
      }

      try {
        const storageManager = StorageManager()
        const container = storageManager.get(StorageKey.ApiCacheEntries)

        if (container && typeof container === 'object') {
          const now = Date.now()
          const validEntries = new Map()

          // Filter out expired entries while loading
          for (const [key, entry] of Object.entries(container)) {
            if (entry && typeof entry === 'object' && typeof entry.expiresAt === 'number') {
              if (entry.expiresAt >= now) {
                validEntries.set(key, entry)
              }
            }
          }

          this.entries = validEntries

          // If we filtered out expired entries, save the cleaned container back
          if (validEntries.size !== Object.keys(container).length) {
            this._saveToStorage()
          }

          this._initialized = true

          return true
        } else {
          // No existing cache, initialize empty
          this.entries = new Map()
          this._initialized = true

          return true
        }
      } catch (err) {
        console.error('[useApiCacheStore][_loadFromStorage] Failed to load from storage:', err)

        // Initialize empty cache on error
        this.entries = new Map()
        this._initialized = true

        return false
      }
    },

    /**
     * Saves current cache entries to localStorage
     * Handles errors gracefully to prevent data loss
     * @returns {boolean} True if saved successfully, false otherwise
     */
    _saveToStorage() {
      try {
        const storageManager = StorageManager()

        // Convert Map to plain object for storage
        const container = {}
        for (const [key, entry] of this.entries.entries()) {
          container[key] = entry
        }

        // Use force=true to ensure we can overwrite even if TTL hasn't expired
        // (individual entries have their own TTL, container TTL is 0/permanent)
        const saved = storageManager.set(StorageKey.ApiCacheEntries, container, 0, true, false)

        if (!saved) {
          console.warn(
            '[useApiCacheStore][_saveToStorage] Failed to save to storage (set returned false)'
          )

          return false
        }

        return true
      } catch (err) {
        // Handle quota exceeded or other localStorage errors
        if (err?.name === 'QuotaExceededError' || err?.code === 22) {
          console.error(
            '[useApiCacheStore][_saveToStorage] localStorage quota exceeded, attempting cleanup'
          )

          // Try to clear some expired entries first
          this._cleanupExpiredEntries()

          // Retry saving (only once to avoid infinite loop)
          try {
            const storageManager = StorageManager()
            const container = {}
            for (const [key, entry] of this.entries.entries()) {
              container[key] = entry
            }
            storageManager.set(StorageKey.ApiCacheEntries, container, 0, true, false)

            return true
          } catch (retryErr) {
            console.error(
              '[useApiCacheStore][_saveToStorage] Retry after cleanup also failed:',
              retryErr
            )

            return false
          }
        }

        console.error('[useApiCacheStore][_saveToStorage] Failed to save to storage:', err)

        return false
      }
    },

    /**
     * Removes all expired entries from memory and storage
     * @returns {number} Number of entries cleaned up
     */
    _cleanupExpiredEntries() {
      const now = Date.now()
      let cleanedCount = 0

      for (const [key, entry] of this.entries.entries()) {
        if (entry.expiresAt < now) {
          this.entries.delete(key)
          cleanedCount++
        }
      }

      if (cleanedCount > 0) {
        this._saveToStorage()
      }

      return cleanedCount
    },

    /**
     * Ensures the cache is initialized from storage
     * Called before operations that need the cache to be loaded
     */
    _ensureInitialized() {
      if (!this._initialized) {
        this._loadFromStorage()
      }
    },

    /**
     * Generates a normalized cache key from method, URL path, and params
     * @param {string} method - HTTP method (e.g., 'get', 'post')
     * @param {string} urlPath - URL path
     * @param {any} params - Query parameters object
     * @returns {string} Normalized cache key
     */
    generateKey(method, urlPath, params) {
      const methodLower = String(method || 'get').toLowerCase()
      const path = String(urlPath || '').replace(/^https?:\/\/[^/]+/, '')
      let normalizedParams = ''

      try {
        if (params && typeof params === 'object') {
          const sorted = Object.keys(params)
            .sort()
            .reduce((acc, key) => {
              acc[key] = params[key]

              return acc
            }, {})

          normalizedParams = JSON.stringify(sorted)
        }
      } catch (e) {
        // If serialization fails, use empty string
        normalizedParams = ''
      }

      return `${methodLower}::${path}::${normalizedParams}`
    },

    /**
     * Stores a value in the cache with optional TTL
     * @param {string} key - Cache key
     * @param {any} value - Value to cache
     * @param {number} [ttlMs] - Time to live in milliseconds (defaults to defaultTtlMs)
     * @returns {boolean} True if stored successfully, false otherwise
     */
    set(key, value, ttlMs) {
      try {
        this._ensureInitialized()

        // Clean up expired entries periodically
        if (this.entries.size > 100) {
          this._cleanupExpiredEntries()
        }

        const now = Date.now()
        const ttl = Number.isFinite(ttlMs) && ttlMs > 0 ? ttlMs : this.defaultTtlMs

        // Deep clone value to prevent mutations using robust cloning
        let clonedValue
        try {
          clonedValue = robustDeepClone(value)

          // Verify the clone is different from the original (sanity check)
          // This helps catch issues where cloning silently failed
          if (
            clonedValue === value &&
            value !== null &&
            value !== undefined &&
            typeof value === 'object'
          ) {
            console.warn(
              '[useApiCacheStore][set] Clone returned same reference, this may indicate a cloning issue'
            )

            // For safety, try JSON fallback as last resort
            try {
              clonedValue = JSON.parse(JSON.stringify(value))
            } catch (jsonErr) {
              // If JSON also fails, we have a serious issue
              console.error(
                '[useApiCacheStore][set] Both robustDeepClone and JSON.stringify failed, data may be corrupted:',
                jsonErr
              )

              // Still store it but log the issue
              clonedValue = value
            }
          }
        } catch (cloneErr) {
          // If robust cloning fails completely, try JSON fallback
          console.warn(
            '[useApiCacheStore][set] robustDeepClone failed, attempting JSON fallback:',
            cloneErr
          )
          try {
            clonedValue = JSON.parse(JSON.stringify(value))
          } catch (jsonErr) {
            // Both methods failed - this is a critical error
            console.error(
              '[useApiCacheStore][set] All cloning methods failed. This object cannot be safely cached:',
              {
                error: jsonErr,
                valueType: typeof value,
                valueConstructor: value?.constructor?.name,
                valueKeys:
                  value && typeof value === 'object' ? Object.keys(value).slice(0, 10) : null,
              }
            )

            // Still cache it but with a warning - better than losing data
            clonedValue = value
          }
        }

        const entry = {
          value: clonedValue,
          expiresAt: now + ttl,
        }

        this.entries.set(key, entry)

        // Save to localStorage (with error handling)
        const saved = this._saveToStorage()
        if (!saved) {
          // Still keep in memory even if localStorage save fails
          console.warn(
            '[useApiCacheStore][set] Failed to save to localStorage, keeping in memory only'
          )
        }

        return true
      } catch (err) {
        console.error('[useApiCacheStore][set] Failed to set cache entry:', err)

        return false
      }
    },

    /**
     * Retrieves a value from the cache if it exists and hasn't expired
     * @param {string} key - Cache key
     * @returns {any|undefined} Cached value or undefined if not found/expired
     */
    get(key) {
      try {
        this._ensureInitialized()

        const entry = this.entries.get(key)
        if (!entry) {
          return undefined
        }

        const now = Date.now()
        if (entry.expiresAt < now) {
          // Entry expired, remove it
          this.entries.delete(key)

          // Trigger cleanup if cache is getting large (batch cleanup for efficiency)
          if (this.entries.size > 50) {
            this._cleanupExpiredEntries()
          }

          return undefined
        }

        // Deep clone before returning to prevent mutations using robust cloning
        try {
          const clonedValue = robustDeepClone(entry.value)

          // Verify the clone is different from the original (sanity check)
          if (
            clonedValue === entry.value &&
            entry.value !== null &&
            entry.value !== undefined &&
            typeof entry.value === 'object'
          ) {
            console.warn(
              '[useApiCacheStore][get] Clone returned same reference, this may indicate a cloning issue'
            )

            // For safety, try JSON fallback as last resort
            try {
              return JSON.parse(JSON.stringify(entry.value))
            } catch (jsonErr) {
              // If JSON also fails, we have a serious issue
              console.error(
                '[useApiCacheStore][get] Both robustDeepClone and JSON.stringify failed:',
                jsonErr
              )

              // Return original but log the issue - this prevents data loss but allows mutations
              // This is a fallback of last resort
              console.warn(
                '[useApiCacheStore][get] Returning original value - mutations may affect cache. Consider investigating the data structure.'
              )

              return entry.value
            }
          }

          return clonedValue
        } catch (cloneErr) {
          // If robust cloning fails completely, try JSON fallback
          console.warn(
            '[useApiCacheStore][get] robustDeepClone failed, attempting JSON fallback:',
            cloneErr
          )
          try {
            return JSON.parse(JSON.stringify(entry.value))
          } catch (jsonErr) {
            // Both methods failed - this is a critical error
            console.error(
              '[useApiCacheStore][get] All cloning methods failed. Returning original value with mutation risk:',
              {
                error: jsonErr,
                originalError: cloneErr,
                valueType: typeof entry.value,
                valueConstructor: entry.value?.constructor?.name,
                valueKeys:
                  entry.value && typeof entry.value === 'object'
                    ? Object.keys(entry.value).slice(0, 10)
                    : null,
              }
            )

            // Return original but warn - prevents data loss but allows mutations
            // This is better than returning undefined and losing the cached data
            console.warn(
              '[useApiCacheStore][get] WARNING: Returning original value - mutations may affect cache. This should be investigated.'
            )

            return entry.value
          }
        }
      } catch (err) {
        console.error('[useApiCacheStore][get] Failed to get cache entry:', err)

        return undefined
      }
    },

    /**
     * Checks if a cache key has a valid (non-expired) entry
     * @param {string} key - Cache key
     * @returns {boolean} True if entry exists and is not expired
     */
    hasValid(key) {
      try {
        this._ensureInitialized()

        const entry = this.entries.get(key)
        if (!entry) {
          return false
        }

        const now = Date.now()
        if (entry.expiresAt < now) {
          // Entry expired, remove it
          this.entries.delete(key)

          // Trigger cleanup if cache is getting large (batch cleanup for efficiency)
          if (this.entries.size > 50) {
            this._cleanupExpiredEntries()
          }

          return false
        }

        return true
      } catch (err) {
        console.error('[useApiCacheStore][hasValid] Failed to check cache entry:', err)

        return false
      }
    },

    /**
     * Removes a specific cache entry
     * @param {string} key - Cache key to invalidate
     * @returns {boolean} True if entry was removed, false otherwise
     */
    invalidate(key) {
      try {
        this._ensureInitialized()

        const existed = this.entries.has(key)
        if (existed) {
          this.entries.delete(key)
          this._saveToStorage()
        }

        return existed
      } catch (err) {
        console.error('[useApiCacheStore][invalidate] Failed to invalidate cache entry:', err)

        return false
      }
    },

    /**
     * Removes all cache entries that start with the given prefix
     * @param {string} prefix - Prefix to match against cache keys
     * @returns {number} Number of entries removed
     */
    clearNamespace(prefix) {
      try {
        this._ensureInitialized()

        const p = String(prefix || '')
        if (!p) {
          // Empty prefix, don't remove anything (safety)
          return 0
        }

        let removedCount = 0
        const keysToRemove = []

        // Collect keys to remove (don't modify Map while iterating)
        for (const k of this.entries.keys()) {
          if (k.startsWith(p)) {
            keysToRemove.push(k)
          }
        }

        // Remove collected keys
        for (const k of keysToRemove) {
          this.entries.delete(k)
          removedCount++
        }

        if (removedCount > 0) {
          this._saveToStorage()
        }

        return removedCount
      } catch (err) {
        console.error('[useApiCacheStore][clearNamespace] Failed to clear namespace:', err)

        return 0
      }
    },

    /**
     * Clears all cache entries (for testing/debugging)
     * @returns {boolean} True if cleared successfully
     */
    clear() {
      try {
        this.entries.clear()

        const storageManager = StorageManager()

        storageManager.remove(StorageKey.ApiCacheEntries)
        this._initialized = true

        return true
      } catch (err) {
        console.error('[useApiCacheStore][clear] Failed to clear cache:', err)

        return false
      }
    },
  },
})
